import { defineStore } from "pinia";

import { CaseStudiesState, Hit, Filters } from "../interfaces/knowledge-hub";
import { Filter } from "../interfaces/facets";

import { useSearchParameters, useTypesenseClient } from "../composables/typesense";

export const useCaseStudiesStore = defineStore("caseStudies", {
	state: (): CaseStudiesState => ({
		loading: false,
		facets: [],
		filters: {
			searchQuery: "",
			filterQuery: "",
			date: {
				field: "postDateYear",
				values: [],
				combination: "&&",
			},
			pillars: {
				field: "pillars",
				values: [],
				combination: "&&",
			},
			industries: {
				field: "industries",
				values: [],
				combination: "&&",
			},
			solutions: {
				field: "solutions",
				values: [],
				combination: "&&",
			},
			type: {
				field: "type",
				values: [],
				combination: "&&",
			},
		},
		pagination: {
			current: 0,
			totalPages: null,
			totalResults: null,
			foundResults: null,
			itemsPerPage: 24,
		},
		sorting: {
			type: "post_date_timestamp",
			order: "desc",
		},
		index: import.meta.env.VITE_STUDIES_INDEX ?? "wdp_production_studies",
		studies: [],
	}),
	actions: {
		async fetch(reset = false) {
			this.loading = true;
			this.setFiltersQuery();

			const client = useTypesenseClient();
			const searchParameters = useSearchParameters(this.filters, this.sorting);

			if (!reset && this.pagination.current) {
				this.pagination.current += 1;
			} else {
				this.pagination.current = 1;
				this.pagination.totalPages = 0;
				this.pagination.totalResults = null;
				this.studies = [];
			}

			const searchRequests = {
				searches: [
					{
						highlight_full_fields: "description,meta,slug",
						collection: this.index,
						facet_by: "postDateYear",
						page: this.pagination.current,
						per_page: this.pagination.itemsPerPage,
						...searchParameters,
					},
					{
						query_by: "description,meta,slug",
						sort_by: "_text_match:desc",
						collection: this.index,
						q: "*",
						facet_by: "postDateYear",
						page: 1,
						per_page: 1,
						filter_by: `site:${window.language}`,
					},
				],
			};

			await client.multiSearch
				.perform(searchRequests)
				.then((results: any) => {
					const filteredResults = results.results[0];
					const facets = results.results[1].facet_counts;

					this.facets = facets;
					this.pagination.current = filteredResults.page;
					this.pagination.totalPages = this.pagination.itemsPerPage
						? Math.ceil(filteredResults.found / this.pagination.itemsPerPage)
						: 0;
					this.pagination.foundResults = filteredResults.found;
					this.pagination.totalResults = filteredResults.out_of;

					if (filteredResults.hits) {
						const hits: Array<Hit> = filteredResults.hits;

						// set results to the projects if it's a reset otherwise add the filteredResults to the existing ones
						if (reset) {
							this.studies = hits;
						} else {
							this.studies = [...(this.studies || []), ...hits];
						}
					}
				})
				.catch((error) => {
					console.error("Typesense search failed:", error);
				})
				.finally(() => {
					this.loading = false;
				});
		},
		setFilter(type: keyof Filters, value: string) {
			const settedFilter: Filter | string = this.filters[type];

			if (typeof settedFilter !== "string") {
				if (settedFilter.values.indexOf(value) < 0) {
					settedFilter.values.push(value);
				} else {
					settedFilter.values = settedFilter.values.filter((item: string) => item !== value);
				}
			}

			this.fetch(true);
		},
		setFiltersQuery() {
			const filterParts: string[] = [];

			filterParts.push(`site:=${window.language}`);
			filterParts.push(`hideInFrontEndSearch:=false`);

			if (this.filters.date.values.length > 0) {
				filterParts.push(`${this.filters.date.field}:=[${this.filters.date.values.join(",")}]`);
			}

			this.filters.filterQuery = filterParts.join(" && ");
		},
	},
});
